export const PRODUCT_FEATURES = [
  'core_study',
  'file_transfer',
  'cloud_backup',
  'plan',
  'review',
  'ai_extract',
  'exam_slots_over_three',
  'notifications',
  'rooms',
  'optional_iap_purchase',
  'pass_season',
] as const;

export type ProductFeature = (typeof PRODUCT_FEATURES)[number];
export type FeatureMode = 'disabled' | 'enabled' | 'maintenance';
export type AccessClass = 'free' | 'entitlement';
export type NetworkMode = 'online' | 'offline';
export type EntitlementStatus = 'active' | 'expired' | 'refunded' | 'revoked';

export type FeaturePolicy = {
  mode: FeatureMode;
  access: AccessClass;
  serverRequired: boolean;
};

export type EntitlementSnapshot = {
  status: EntitlementStatus;
  productId: string;
  verifiedAt: string;
  startsAt: string;
  expiresAt: string;
  offlineGraceUntil: string;
  lastObservedAt: string;
};

export type FeatureDecision =
  | { outcome: 'available'; authority: 'config' | 'server_entitlement' }
  | { outcome: 'offline_grace'; authority: 'cached_entitlement'; validUntil: string }
  | {
      outcome: 'paywall';
      authority: 'server_entitlement';
      reason: 'missing' | 'not_started' | 'expired' | 'refunded' | 'revoked';
    }
  | { outcome: 'disabled'; authority: 'config' }
  | { outcome: 'maintenance'; authority: 'config' }
  | {
      outcome: 'reconnect_required';
      authority: 'server_entitlement';
      reason: 'server_required' | 'missing_cache' | 'stale_cache' | 'clock_rollback';
    }
  | { outcome: 'refresh_required'; authority: 'server_entitlement'; reason: 'stale_online' };

export const DEFAULT_FEATURE_POLICIES: Readonly<Record<ProductFeature, FeaturePolicy>> = {
  pass_season: { mode: 'disabled', access: 'entitlement', serverRequired: true },
  core_study: { mode: 'enabled', access: 'free', serverRequired: false },
  file_transfer: { mode: 'enabled', access: 'free', serverRequired: false },
  cloud_backup: { mode: 'enabled', access: 'free', serverRequired: true },
  // The core "plan → record → review" loop must work before a user has any
  // reason to trust a new local-first product with payment details.
  plan: { mode: 'enabled', access: 'free', serverRequired: false },
  review: { mode: 'enabled', access: 'free', serverRequired: false },
  // D17 reserves only these supplemental, server-cost-bearing rights for a
  // future IAP offer. They stay disabled until a verified catalog, receipt
  // ledger, settlement and a new offer ADR exist.
  ai_extract: { mode: 'disabled', access: 'entitlement', serverRequired: true },
  exam_slots_over_three: { mode: 'disabled', access: 'entitlement', serverRequired: true },
  notifications: { mode: 'enabled', access: 'free', serverRequired: true },
  rooms: { mode: 'enabled', access: 'free', serverRequired: true },
  optional_iap_purchase: { mode: 'disabled', access: 'free', serverRequired: true },
};

const ONLINE_MAX_AGE_MS = 15 * 60 * 1_000;

function instant(value: string): number | undefined {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function paywallReason(
  snapshot: EntitlementSnapshot | undefined,
  now: number,
): Extract<FeatureDecision, { outcome: 'paywall' }>['reason'] | undefined {
  if (!snapshot) return 'missing';
  if (snapshot.status === 'refunded') return 'refunded';
  if (snapshot.status === 'revoked') return 'revoked';
  const startsAt = instant(snapshot.startsAt);
  const expiresAt = instant(snapshot.expiresAt);
  if (startsAt === undefined || expiresAt === undefined) return 'missing';
  if (now < startsAt) return 'not_started';
  if (snapshot.status === 'expired' || now > expiresAt) return 'expired';
  return undefined;
}

export function evaluateFeatureAccess(options: {
  policy: FeaturePolicy;
  network: NetworkMode;
  now: string;
  entitlement?: EntitlementSnapshot;
  onlineMaxAgeMs?: number;
}): FeatureDecision {
  if (options.policy.mode === 'disabled') return { outcome: 'disabled', authority: 'config' };
  if (options.policy.mode === 'maintenance') {
    return { outcome: 'maintenance', authority: 'config' };
  }
  if (options.policy.access === 'free') {
    if (options.network === 'offline' && options.policy.serverRequired) {
      return {
        outcome: 'reconnect_required',
        authority: 'server_entitlement',
        reason: 'server_required',
      };
    }
    return { outcome: 'available', authority: 'config' };
  }

  const now = instant(options.now);
  if (now === undefined) {
    return {
      outcome: 'reconnect_required',
      authority: 'server_entitlement',
      reason: 'stale_cache',
    };
  }
  const snapshot = options.entitlement;
  const blocked = paywallReason(snapshot, now);
  if (blocked) return { outcome: 'paywall', authority: 'server_entitlement', reason: blocked };
  if (!snapshot) {
    return { outcome: 'paywall', authority: 'server_entitlement', reason: 'missing' };
  }
  const verifiedAt = instant(snapshot.verifiedAt);
  const expiresAt = instant(snapshot.expiresAt);
  const offlineGraceUntil = instant(snapshot.offlineGraceUntil);
  const lastObservedAt = instant(snapshot.lastObservedAt);
  if (
    verifiedAt === undefined ||
    expiresAt === undefined ||
    offlineGraceUntil === undefined ||
    lastObservedAt === undefined
  ) {
    return {
      outcome: 'reconnect_required',
      authority: 'server_entitlement',
      reason: 'stale_cache',
    };
  }
  if (now < lastObservedAt) {
    return {
      outcome: 'reconnect_required',
      authority: 'server_entitlement',
      reason: 'clock_rollback',
    };
  }
  if (options.network === 'online') {
    if (now - verifiedAt > (options.onlineMaxAgeMs ?? ONLINE_MAX_AGE_MS)) {
      return {
        outcome: 'refresh_required',
        authority: 'server_entitlement',
        reason: 'stale_online',
      };
    }
    return { outcome: 'available', authority: 'server_entitlement' };
  }
  if (options.policy.serverRequired) {
    return {
      outcome: 'reconnect_required',
      authority: 'server_entitlement',
      reason: 'server_required',
    };
  }
  const validUntil = Math.min(expiresAt, offlineGraceUntil);
  if (now <= validUntil) {
    return {
      outcome: 'offline_grace',
      authority: 'cached_entitlement',
      validUntil: new Date(validUntil).toISOString(),
    };
  }
  return {
    outcome: 'reconnect_required',
    authority: 'server_entitlement',
    reason: 'stale_cache',
  };
}

export function resolveFeature(
  feature: ProductFeature,
  overrides: Partial<Record<ProductFeature, FeatureMode>> = {},
): FeaturePolicy {
  const policy = DEFAULT_FEATURE_POLICIES[feature];
  const mode = overrides[feature];
  return mode === undefined ? policy : { ...policy, mode };
}
